import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getParamAsInt } from "../../../../app/utils";
import { useAppSelector } from "../../../../app/hooks";
import { useScrollToSection } from "../../../../app/useScrollToSection";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import { Loading } from "../../../../components/Loading";
import { DownloadButton } from "../../shared/DownloadButton";
import EssayDraftHistory from "./essayDraftHistory/EssayDraftHistory";
import { MonthlyScoresTable } from "./MonthlyScoresTable";
import { MonthlyScoresChartWithFilters } from "./MonthlyScoresChartWithFilters";
import { useCourseProgressReportQuery } from "./progressReportApiSlice";

export const CLASS_AVERAGE_MONTHLY_SCORES = "average-monthly-scores";

export default function CourseProgressReport() {
	const [queryParams, setSearchParams] = useSearchParams();
	const courseIdParam = getParamAsInt(queryParams, "courseId");

	const { aspUrl } = useAppSelector((state) => state.site);
	useScrollToSection();

	const [view, setView] = useState("react");

	const result = useCourseProgressReportQuery(courseIdParam ? { courseId: courseIdParam } : {});
	const data = result.data;

	return (
		<main>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Class Progress Report</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
				/>
			</div>
			{view == "react" && (
				<>
					<section id={CLASS_AVERAGE_MONTHLY_SCORES} className="mt-8">
						<h2>Average Monthly Scores</h2>
						<p className="mt-4">
							The table below displays average holistic scores for the current school
							year by month. To view the monthly scores for a specific student, click
							on the student’s name. Other scores can be viewed in the graph. Use the
							drop-down menus above the graph to display monthly scores for a specific
							category. Advanced filtering options display scores for previous years,
							for first drafts, last drafts, or both. Please note that Constructed
							Response scores are not included.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full mt-4">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadMonthlyProgressFilteredByCourse?courseId=${data.courseId}&beginSchoolYear=${data.beginSchoolYear}&endSchoolYear=${data.endSchoolYear}`}
										disabled={data.monthlyProgressByWriter.length == 0}
									/>
								</div>
								<MonthlyScoresTable
									className="mt-4"
									courseId={data.courseId}
									writerData={data.monthlyProgressByWriter?.toSorted((a, b) => {
										return a.writerName.localeCompare(b.writerName);
									})}
									classData={data.monthlyProgressForCourse}
								/>
								<MonthlyScoresChartWithFilters
									className="mt-4"
									courseId={data.courseId}
									data={data}
								/>
							</>
						)}
					</section>
					<EssayDraftHistory courseId={courseIdParam} />
				</>
			)}
			{view == "raw" && <RtkqResultViewer heading="Progress Report Data" result={result} />}
		</main>
	);
}

export function studentProgressLink(courseId?: number, writerId?: number, section?: string) {
	let link = "/reports/student/progress?";
	if (courseId) link += `courseId=${courseId}&`;
	if (writerId) link += `writerId=${writerId}&`;
	if (section) link += `section=${section}&`;
	return link.slice(0, -1);
}
